import { buildDocumentSourceMetadata } from '../utils/merge-docs-by-identity';
import { classifyDocumentType, loadActiveBusinessIdentity } from './document-type-classifier';
import { buildPhotoTextDocument, upsertReferenceDocument } from './image-text-documents';
import { resolvePhotoDataUrl } from './photo-auto-assign';
import { dataUrlToBlob } from './photo-blob-store';
import { buildStage1DeterministicClassificationMetadata } from './stage1-batch-classification';
import { routeStage1ImagesByText } from './stage1-image-text-routing';
import { createSelectedFileScope, fileMatchesSelectedScope } from './selected-file-scope';
import {
    assertEvidencePreparationCurrent, checkpointPreparedEvidence,
    type ProjectEvidencePreparationOptions, type ProjectEvidencePreparationResult,
} from './project-evidence-preparation-types';

type Row = Record<string, any>;

/** The mark the Import Projects row builder leaves on a photo it has not routed yet. */
export const IMAGE_TEXT_ROUTING_PENDING = 'pending';

/**
 * A Stage 1 drop checks every image for writing before it becomes a photo
 * (routeStage1ImagesByText, Peter 2026-10-13: an RFQ can arrive as a photo, a
 * scan or a screenshot). A folder import sent every image to the photo lane,
 * so such a request could never be the RFQ (Peter, 2026-10-23: the routes must
 * behave the same). This runs the same check on the folder photos not routed
 * yet and applies the same outcome: a document image leaves the photo lane for
 * the documents, a photo that shows writing also gets its text row, and every
 * other photo stays. Each is checked once.
 */
export async function prepareProjectImageTextRouting(
    options: ProjectEvidencePreparationOptions,
    overrides: { route?: typeof routeStage1ImagesByText; resolveDataUrl?: (photo: Row, projectId: string) => Promise<string | null> } = {},
): Promise<ProjectEvidencePreparationResult> {
    assertEvidencePreparationCurrent(options);
    const project = options.project;
    const photos: Row[] = Array.isArray(project.photos) ? project.photos : [];
    const scope = createSelectedFileScope(options.selectedFileIds);
    const pending = photos.filter(photo => photo?.imageTextRouting === IMAGE_TEXT_ROUTING_PENDING
        && fileMatchesSelectedScope(photo, scope));
    if (!options.apiKey || pending.length === 0) return { project, changed: false };
    options.onProgress?.(0, 'Checking images for text...');

    const resolveDataUrl = overrides.resolveDataUrl || ((photo: Row, projectId: string) => resolvePhotoDataUrl(photo, projectId));
    const dataUrlOf = new Map<Row, string>();
    const files: File[] = [];
    const photoOf = new Map<File, Row>();
    const fileOf = new Map<Row, File>();
    for (const photo of pending) {
        const dataUrl = await resolveDataUrl(photo, project.id);
        assertEvidencePreparationCurrent(options);
        // Bytes this browser cannot reach stay a photo, unchecked, as Stage 1 keeps an undecodable image.
        if (!dataUrl) continue;
        const blob = await dataUrlToBlob(dataUrl);
        const file = new File([blob], String(photo.name || photo.originalName || 'image.jpg'), { type: blob.type || 'image/jpeg' });
        dataUrlOf.set(photo, dataUrl);
        photoOf.set(file, photo);
        fileOf.set(photo, file);
        files.push(file);
    }
    const routing = await (overrides.route || routeStage1ImagesByText)(files, {
        apiKey: options.apiKey, model: options.model, projectId: project.id,
        shouldStop: () => !!options.signal?.aborted,
    });
    assertEvidencePreparationCurrent(options);

    const verdictOf = new Map<Row, string>();
    for (const [file, verdict] of routing.verdicts) verdictOf.set(photoOf.get(file)!, verdict);
    const identity = await loadActiveBusinessIdentity();
    const now = new Date();
    const imageDocuments: Row[] = [];
    let documents: Row[] = Array.isArray(project.documents) ? project.documents : [];
    const nextPhotos: Row[] = [];
    for (const photo of photos) {
        const verdict = verdictOf.get(photo);
        if (!pending.includes(photo)) { nextPhotos.push(photo); continue; }
        const file = fileOf.get(photo);
        const text = file ? routing.textByFile.get(file) || '' : '';
        if (verdict === 'document') {
            // The row Stage 1's handleDocsFiles builds for an image document.
            const classification = classifyDocumentType('', text, identity);
            const type = classification.class === 'other' && text.length > 100 ? 'reference' : classification.class;
            const name = String(photo.name || photo.originalName || 'image.jpg');
            imageDocuments.push({
                id: photo.id,
                name,
                originalName: photo.originalName || name,
                type,
                ext: (name.split('.').pop() || 'jpg').toLowerCase(),
                content: text,
                data: dataUrlOf.get(photo),
                size: photo.originalSize ?? null,
                isImageDoc: true,
                addedAt: Date.now(),
                relativePath: photo.relativePath,
                preprocessedAt: now.toISOString(),
                ...buildDocumentSourceMetadata({ name, relativePath: photo.relativePath, quoteMakerFileId: photo.id }),
                ...buildStage1DeterministicClassificationMetadata(classification),
            });
            continue;
        }
        const routed = { ...photo, imageTextRouting: verdict || 'photo-not-checked' };
        nextPhotos.push(routed);
        if (verdict === 'both' && text) {
            documents = upsertReferenceDocument(documents, buildPhotoTextDocument({ photo: routed, text, kind: 'both', now }));
        }
    }
    const next = { ...project, photos: nextPhotos, documents: [...documents, ...imageDocuments] };
    return { project: await checkpointPreparedEvidence(options, next), changed: true };
}
